type Point = [number, number];

export type RouteGeometrySegment = {
  coordinates: Point[];
};

function routePath(points: Point[], project: (point: Point) => Point) {
  return points
    .map((point, index) => {
      const [x, y] = project(point);
      return `${index ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

export function RouteGeometryPreview({
  segments,
  ariaLabel,
  className = '',
  showStops = true,
}: {
  segments: RouteGeometrySegment[];
  ariaLabel: string;
  className?: string;
  showStops?: boolean;
}) {
  const coordinates = segments.flatMap((segment) => segment.coordinates);
  if (coordinates.length < 2) return null;

  const averageLatitude =
    coordinates.reduce((sum, coordinate) => sum + coordinate[1], 0) / coordinates.length;
  const longitudeScale = Math.cos((averageLatitude * Math.PI) / 180);
  const projected = coordinates.map(
    ([longitude, latitude]) => [longitude * longitudeScale, latitude] as Point,
  );
  const minX = Math.min(...projected.map((point) => point[0]));
  const maxX = Math.max(...projected.map((point) => point[0]));
  const minY = Math.min(...projected.map((point) => point[1]));
  const maxY = Math.max(...projected.map((point) => point[1]));
  const width = Math.max(maxX - minX, 0.001);
  const height = Math.max(maxY - minY, 0.001);
  const scale = Math.min(540 / width, 240 / height);
  const renderedWidth = width * scale;
  const renderedHeight = height * scale;
  const offsetX = (640 - renderedWidth) / 2;
  const offsetY = (320 - renderedHeight) / 2;
  const project = ([longitude, latitude]: Point): Point => [
    offsetX + (longitude * longitudeScale - minX) * scale,
    offsetY + (maxY - latitude) * scale,
  ];
  const first = project(coordinates[0]!);
  const last = project(coordinates.at(-1)!);

  return (
    <svg
      className={`real-route-preview${className ? ` ${className}` : ''}`}
      viewBox="0 0 640 320"
      role="img"
      aria-label={ariaLabel}
      preserveAspectRatio="xMidYMid meet"
    >
      <title>{ariaLabel}</title>
      {segments.map((segment, index) => (
        <g key={index}>
          <path
            className="real-route-preview__casing"
            d={routePath(segment.coordinates, project)}
          />
          <path className="real-route-preview__line" d={routePath(segment.coordinates, project)} />
        </g>
      ))}
      {showStops && (
        <>
          <circle className="real-route-preview__stop" cx={first[0]} cy={first[1]} r="8" />
          <circle className="real-route-preview__stop is-finish" cx={last[0]} cy={last[1]} r="10" />
        </>
      )}
    </svg>
  );
}
